import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDateParserFormatter, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateFRParserFormatter } from './../inspections-scheduled/ngb-date-fr-parser-formatter';
import { JobcostingService } from '../../services/jobcosting/jobcosting.service';
import { CommonService } from '../../services/common/common.service';
import { REPORT_DATE_RANGES, downloadCsv, matches, pageNumbers, toNumber } from '../../report-view';

/** The money columns, in table order: API field, CSV header, column group. */
const MONEY_COLUMNS = [
  { key: 'inhouseCrew', label: 'Crew Item Charges', group: 'crew' },
  { key: 'crewWages', label: 'Crew Wages', group: 'crew' },
  { key: 'crewMaerials', label: 'Crew Materials', group: 'crew' },
  { key: 'treatItemCharges', label: 'Treatment Item Charges', group: 'treat' },
  { key: 'treatWages', label: 'Treatment Wages', group: 'treat' },
  { key: 'treatMaterials', label: 'Treatment Materials', group: 'treat' },
  { key: 'subContractedItemCharges', label: 'Subcontractor Item Charges', group: 'sub' },
  { key: 'subContractorCosts', label: 'Subcontractor Costs', group: 'sub' },
  { key: 'jobCrewOverhead', label: 'Overhead Crew & Office', group: 'overhead' },
  { key: 'jobOfficeOverhead', label: 'Overhead Sub & Office', group: 'overhead' },
  { key: 'inspectorCommission', label: 'Inspector Commission', group: '' },
  { key: 'totalJobCosts', label: 'Total Job Costs', group: '' },
  { key: 'totalCompletionDiscounts', label: 'Completion Discounts', group: '' },
  { key: 'completionCharges', label: 'Total Job Charges', group: '' },
];

/** Same order for the API's totals (report and per inspector). */
const TOTAL_KEYS = ['subTotalCrewItemCharges', 'subTotalCrewWages', 'subTotalCrewMaterials', 'subTotalTreatItemCharges',
  'subTotalTreatWages', 'subTotalTreatMaterials', 'subTotalSubContractedItemCharges', 'subTotalubContractorCosts',
  'subTotalJobCrewOverhead', 'subTotaljobOfficeOverhead', 'subTotalInspectorCommission', 'subTotalJobCosts',
  'subTotalCompletionDiscounts', 'subTotalJobCharges'];
const GROUP_TOTAL_KEYS = ['single_subTotalCrewItemCharges_display', 'single_subTotalCrewWages_display', 'single_subTotalCrewMaterials_display',
  'single_subTotalTreatItemCharges_display', 'single_subTotalTreatWages_diplay', 'single_subTotalTreatMaterials_display',
  'single_subTotalSubContractedItemCharges_display', 'single_subTotalubContractorCosts_dispaly', 'single_subTotalJobCrewOverhead_display',
  'single_subTotaljobOfficeOverhead_dispaly', 'single_subTotalInspectorCommission_dispaly', 'single_subTotalJobCosts_dispaly',
  'single_subTotalCompletionDiscounts_dispaly', 'single_subTotalJobCharges_dispaly'];

/**
 * Job Costing (mockup reports/job-costing). Same API (jobcost), filters, notable / break-out options,
 * date presets and inspector rule as before; the Completion Discounts column and the per-inspector
 * subtotals are kept. Summary tiles come from the API's totals. Also embedded on the old admin
 * dashboard / home widgets ([count]), where the page head is hidden as before.
 */
@Component({
  selector: 'app-jobcosting',
  templateUrl: './jobcosting.component.html',
  providers: [{ provide: NgbDateParserFormatter, useClass: NgbDateFRParserFormatter }, JobcostingService, CommonService],
  styleUrls: ['./jobcosting.component.scss']
})
export class JobcostingComponent implements OnChanges, OnInit {
  @Input() count: number;
  readonly embedded: boolean;
  readonly dateRanges = REPORT_DATE_RANGES;
  readonly pageSizes = [10, 25, 50, 100];
  readonly moneyColumns = MONEY_COLUMNS;
  readonly totalKeys = TOTAL_KEYS;
  readonly groupTotalKeys = GROUP_TOTAL_KEYS;

  report = this.emptyFilters();
  branches = [];
  inspectors = [];
  /** Branch and Inspector filters: not for inspectors (the API limits them to their own jobs). */
  inspectorCheck = false;
  loaded = false;
  errordetails = false;
  loading = false;
  breakOut = false;
  /** Flat jobs, or inspector groups ({ name, jobs, single_* totals }) when broken out. */
  jobs: any[] = [];
  groups: any[] = [];
  total: any = {};
  search = '';
  pageSize = 25;
  page = 1;
  today = new Date();

  constructor(private _router: Router, private calendar: NgbCalendar, private jobcostingService: JobcostingService,
    private commonService: CommonService) {
    this.embedded = _router.url.split('?')[0] !== '/accounting-report-job-costing';
  }

  ngOnInit() {
    this.commonService.getBranch().subscribe(data => this.branches = data);
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    this.inspectorCheck = !!user && user.user_level != 'inspector';
    if (this.inspectorCheck) {
      this.commonService.getInspectors().subscribe(data => this.inspectors = data);
    }
  }

  ngOnChanges() {
    // Date range picked on the dashboard widget.
    if (this.count) {
      this.report.dateRange = this.count;
      this.dateRangeSubmit(this.count);
      this.jobCostingReport();
    }
  }

  private emptyFilters() {
    return { branch: '', inspector: '', start: null, end: null, dateRange: 0 as any, notable_jobs: 0, break_out_by_inspector: false };
  }

  setNotable(checked: boolean) {
    this.report.notable_jobs = checked ? 1 : 0;
  }

  jobCostingReport() {
    if (!this.report.start || !this.report.end) {
      this.errordetails = true;
      return false;
    }
    this.errordetails = false;
    this.loading = true;
    this.jobcostingService.getJobCost(this.report).subscribe(
      data => {
        this.loading = false;
        this.loaded = true;
        this.today = new Date();
        const list = data && data.jobs ? (Array.isArray(data.jobs) ? data.jobs : Object.keys(data.jobs).map(k => data.jobs[k])) : [];
        this.breakOut = !!(data && data.break_out_by_inspector);
        this.groups = this.breakOut ? list.map(g => Object.assign({}, g, { jobs: g.jobs || [] })) : [];
        this.jobs = this.breakOut ? [] : list;
        this.total = (data && data.totals) || {};
        this.page = 1;
      },
      () => this.loading = false
    );
  }

  clearJobCosting() {
    this.report = this.emptyFilters();
    this.search = '';
    this.errordetails = false;
  }

  dateRangeSubmit(value) {
    this.report.end = this.calendar.getToday();
    this.report.start = this.commonService.dateRangeSubmit(value);
  }

  // ---- Cells ----

  /** "1,234.00   (12.5 %)" -> amount "$1,234.00" and the percentage shown under it. */
  money(value: any): { amount: string, pct: string } {
    const text = value === null || value === undefined ? '' : String(value);
    const m = /^\s*\$?\s*(-?[\d,]*\.?\d+)\s*(?:\(\s*([^)]*?)\s*\))?/.exec(text);
    if (!m) {
      return { amount: text, pct: '' };
    }
    const n = toNumber(m[1]) || 0;
    return { amount: (n < 0 ? '-$' : '$') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), pct: (m[2] || '').replace(/\s+%/, '%') };
  }

  isNegative(value: any): boolean {
    return (toNumber(String(value || '').split('(')[0]) || 0) < 0;
  }

  jobMatches(job: any): boolean {
    return matches(this.search, [job.propertyAddress, job.inspector, job.manager_note, job.completionDate, job.job_id]);
  }

  // ---- Flat list: search + paging ----

  get filteredJobs(): any[] {
    return this.jobs.filter(j => this.jobMatches(j));
  }

  get pageJobs(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredJobs.slice(start, start + this.pageSize);
  }

  // ---- Broken out: search keeps a group's matching jobs; paging is by inspector ----

  get filteredGroups(): any[] {
    return this.groups
      .map(g => Object.assign({}, g, { visibleJobs: matches(this.search, [g.name]) && this.search.trim() ? g.jobs : g.jobs.filter(j => this.jobMatches(j)) }))
      .filter(g => g.visibleJobs.length);
  }

  get pageGroups(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredGroups.slice(start, start + this.pageSize);
  }

  get itemCount(): number {
    return this.breakOut ? this.filteredGroups.length : this.filteredJobs.length;
  }

  get jobCount(): number {
    return this.breakOut ? this.filteredGroups.reduce((n, g) => n + g.visibleJobs.length, 0) : this.filteredJobs.length;
  }

  get pages(): number {
    return Math.max(1, Math.ceil(this.itemCount / this.pageSize));
  }

  get pageNumbers(): number[] {
    return pageNumbers(this.page, this.pages);
  }

  goTo(page: number) {
    if (page >= 1 && page <= this.pages) {
      this.page = page;
    }
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.page = 1;
  }

  onSearch() {
    this.page = 1;
  }

  // ---- Summary tiles (the API's report totals) ----

  get charges(): number {
    return toNumber(this.total.subTotalJobCharges) || 0;
  }

  get costs(): number {
    return toNumber(this.total.subTotalJobCosts) || 0;
  }

  get profit(): number {
    return this.charges - this.costs;
  }

  get profitability(): number {
    return toNumber(this.total.subTotalprofitability) || 0;
  }

  get hasResults(): boolean {
    return this.jobs.length > 0 || this.groups.length > 0;
  }

  plural(n: number, one: string, many: string): string {
    return n + ' ' + (n === 1 ? one : many);
  }

  exportCsv() {
    const header = ['Date', 'Report #', 'Address', 'Inspector', 'Manager Notes'].concat(MONEY_COLUMNS.map(c => c.label), ['Profitability']);
    const line = (j: any) => [j.completionDate, j.job_id, j.propertyAddress, j.inspector, j.manager_note]
      .concat(MONEY_COLUMNS.map(c => this.money(j[c.key]).amount), [j.profitability]);
    const rows: any[][] = [header];
    if (this.breakOut) {
      this.filteredGroups.forEach(g => g.visibleJobs.forEach(j => rows.push(line(j))));
    } else {
      this.filteredJobs.forEach(j => rows.push(line(j)));
    }
    downloadCsv('job-costing.csv', rows);
  }
}
